import {
  useLayoutEffect,
  useRef,
  type FocusEvent,
  type MouseEvent,
  type RefObject,
} from "react";

import { useCombobox } from "./Combobox";

import { SelectTriggerBase } from "../SelectTrigger/internal/SelectTriggerBase";
import { usePopoverContext } from "../Popover";
import { LabelProps } from "../Label";
import { TextFieldProps } from "../TextField";
import { ChipProps } from "../Chip";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

import { supportsPopover } from "../../internal/functions/supportsPopover";
import { ComboboxLegacyTrigger } from "../ComboboxLegacy/ComboboxLegacyTrigger";

/**
 * Props for the ComboboxTrigger component
 * @template Item - The type of items in the combobox
 */
type ComboboxTriggerProps<Item = any> = Omit<
  TextFieldProps,
  "disabled" | "readOnly" | "showCounter"
> &
  Pick<LabelProps, "required" | "moreInfo" | "openMoreInfo"> &
  LayoutUtilProps & {
    /**
     * Should be a positive integer.
     */
    maxRows?: number;

    /** Allows Combobox/Select to display custom-colored Chips */
    selectedItemProps?: (item: Item) => Partial<ChipProps>;
  } & {
    /** @deprecated use disabled in <Combobox /> */
    disabled?: boolean;
    /** @deprecated use readOnly in <Combobox /> */
    readOnly?: boolean;
  };

export type ComboboxSearchFieldProps = ComboboxTriggerProps;
export type ComboboxSelectTriggerProps = ComboboxTriggerProps;

const ComboboxTrigger = function <Item>(
  props: ComboboxTriggerProps<Item> & { variant?: "combobox" | "select" },
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

  const {
    // variant: variantProp = "combobox",
    className,
    style,
    label,
    size,
    error,
    hint,
    description,
    required,
    moreInfo,
    openMoreInfo,
    prefix,
    suffix,
    maxRows,
    selectedItemProps = () => ({}),
    id,
    placeholder,
    disabled,
    readOnly,
    ...rest
  } = componentProps;

  const {
    select,
    disableClearSelection,
    itemToString,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    selectedItem,
    selectedItems,
    getSelectedItemProps,
    getDropdownProps,
    reset,
    removeSelectedItem,
    selectItem,
    inputValue,
    isDisabled,
    isReadOnly,
    referenceRef,
    closeMenu,
    setIsDisabled,
    setIsReadOnly,
  } = useCombobox();

  // TODO: Figure out how to fire this console.warn only in "development"
  // useEffect(() => {
  //   if (variant !== variantProp) {
  //     if (variant === "select") {
  //       console.warn(
  //         "Combobox.SearchField should be used with non-Select Combobox",
  //       );
  //     }

  //     if (variant === "combobox") {
  //       console.warn(
  //         "Combobox.SelectTrigger should be used with Combobox.Select",
  //       );
  //     }
  //   }
  // }, [variant, variantProp]);

  const context = usePopoverContext();

  useLayoutEffect(() => {
    if (typeof disabled === "undefined") return;
    setIsDisabled(disabled);
  }, [disabled, setIsDisabled]);

  useLayoutEffect(() => {
    if (typeof readOnly === "undefined") return;
    setIsReadOnly(readOnly);
  }, [readOnly, setIsReadOnly]);

  // if we focus out to something not within the reference or content divs
  // then close the popover
  const handleBlur = (e: FocusEvent) => {
    const target = e.relatedTarget ?? e.target;
    const focusedOnReference =
      context?.invoker.ref.current &&
      context?.invoker.ref.current.contains(target);
    const focusedOnContent =
      (context?.popover.ref.current &&
        context?.popover.ref.current.contains(target)) ||
      context?.popover.element?.contains(target);

    if ((!focusedOnReference && !focusedOnContent) || !e.relatedTarget) {
      closeMenu();
    }
  };

  const labelProps = getLabelProps({
    required,
    moreInfo,
    openMoreInfo,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const inputWrapperProps = {
    ...(select
      ? {
          ...getToggleButtonProps({
            disabled: isDisabled,
            readOnly: isReadOnly,
            id,
            ref: context?.invoker.ref as unknown as RefObject<
              HTMLDivElement & HTMLButtonElement
            >,
          }),
        }
      : {
          onClick: () => inputRef.current?.focus(),
          ref: context?.invoker.ref as unknown as RefObject<HTMLDivElement>,
        }),
  };

  const toggleButtonProps = select ? {} : getToggleButtonProps();

  const handleClearButtonClick = (_e: MouseEvent<HTMLButtonElement>) => {
    if (reset != null) {
      reset();
    } else {
      selectItem(null);
    }
  };

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <SelectTriggerBase
      data-anv={select ? "combobox-select-trigger" : "combobox-search-field"}
      className={className}
      style={styleCombined}
      label={label}
      size={size}
      error={error}
      hint={hint}
      description={description}
      prefix={prefix}
      suffix={suffix}
      maxRows={maxRows}
      selectedItemProps={selectedItemProps}
      placeholder={placeholder}
      labelProps={labelProps}
      inputWrapperProps={inputWrapperProps}
      toggleButtonProps={toggleButtonProps}
      itemToString={itemToString}
      removeSelectedItem={removeSelectedItem}
      variant={select ? "select" : "combobox"}
      disableClearSelection={disableClearSelection}
      selectedItem={selectedItem}
      selectedItems={selectedItems}
      inputValue={inputValue}
      disabled={isDisabled}
      readOnly={isReadOnly}
      referenceRef={referenceRef}
      onBlur={handleBlur}
      onClearButtonClick={handleClearButtonClick}
      chipProps={(item: Item, index: number) => {
        if (getSelectedItemProps != null) {
          const { onClick: _, ...chipProps } = getSelectedItemProps({
            selectedItem: item,
            index,
          });
          return chipProps;
        }
        return {};
      }}
      inputProps={
        select
          ? {
              ...(getDropdownProps != null
                ? getDropdownProps({
                    required,
                    preventKeyAction: false,
                  })
                : {
                    required,
                  }),
              ["aria-expanded"]: context?.invoker["aria-expanded"],
              ["aria-details"]: getMenuProps().id,
              ["aria-haspopup"]: context?.invoker["aria-haspopup"],
            }
          : {
              ["aria-expanded"]: context?.invoker["aria-expanded"],
              ["aria-details"]: getMenuProps().id,
              ["aria-haspopup"]: context?.invoker["aria-haspopup"],
              ["aria-controls"]: getMenuProps().id,
              ...(getDropdownProps != null && getInputProps != null
                ? getInputProps(
                    getDropdownProps({
                      ...rest,
                      disabled: isDisabled,
                      readOnly: isReadOnly,
                      required,
                      autoComplete: "off",
                      id,
                      preventKeyAction: false,
                      ref: inputRef,
                    }),
                  )
                : getInputProps != null
                  ? getInputProps({
                      ...rest,
                      disabled: isDisabled,
                      readOnly: isReadOnly,
                      required,
                      autoComplete: "off",
                      id,
                      ref: inputRef,
                    })
                  : {}),
            }
      }
    />
  );
};

/**
 * ComboboxSearchField component for rendering the search input field of a combobox
 *
 * Features:
 * - Provides search input with clear button functionality
 * - Supports multiple selection with chip display
 * - Handles keyboard navigation and accessibility
 * - Integrates with combobox state management
 * - Supports custom styling and layout
 * - Manages focus and blur events
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered search field for combobox
 *
 * @example
 * <Combobox.SearchField
 *   label="Search users"
 *   placeholder="Type to search..."
 *   maxRows={3}
 * />
 */
export const ComboboxSearchField = function <Item>(
  props: ComboboxTriggerProps<Item>,
) {
  if (!supportsPopover())
    return <ComboboxLegacyTrigger {...props} variant="combobox" />;
  return <ComboboxTrigger<Item> {...props} variant="combobox" />;
};

/**
 * ComboboxSelectTrigger component for rendering the trigger field of a select combobox
 *
 * Features:
 * - Provides select input with dropdown trigger
 * - Supports single selection display
 * - Handles keyboard navigation and accessibility
 * - Integrates with combobox state management
 * - Supports custom styling and layout
 * - Manages focus and blur events
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered trigger field for select combobox
 *
 * @example
 * <Combobox.SelectTrigger
 *   label="Select user"
 *   placeholder="Choose a user..."
 * />
 */
export const ComboboxSelectTrigger = function <Item>(
  props: ComboboxTriggerProps<Item>,
) {
  if (!supportsPopover())
    return <ComboboxLegacyTrigger {...props} variant="select" />;
  return <ComboboxTrigger<Item> {...props} variant="select" />;
};
